/**
 * Runs a function (sync or promise-returning) and retries it when it throws or rejects.
 *
 * @param {() => any} fn - Function to run; called again on each retry
 * @param {object} [options]
 * @param {number} [options.maxRetries=3] - Max retry attempts after the initial call
 * @param {(error: any) => boolean} [options.shouldRetry] - Decides whether an error is retryable
 * @param {(retryCount: number) => void | Promise<void>} [options.onRetry] - Called before each
 *   retry; awaited so async cleanup finishes before the next attempt
 * @returns {Promise<any>} Resolves with fn's value, or rejects with the last error
 */
const withRetry = async (
  fn,
  { maxRetries = 3, shouldRetry = () => false, onRetry } = {},
) => {
  const attempt = async (retryCount) => {
    try {
      return await fn();
    } catch (error) {
      if (retryCount >= maxRetries || !shouldRetry(error)) {
        throw error;
      }
      await onRetry?.(retryCount + 1);
      return attempt(retryCount + 1);
    }
  };
  return attempt(0);
};

export default withRetry;
